import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { selectMatterById } from '@/matter/store/selectors'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectMedSummaryAssetForMatter } from '@/medical-billing-summaries/asset/store/selectors'
import MedSummaryAssetChatButton from '@/medical-billing-summaries/asset/components/MedSummaryAssetChatButton'
import MedSummaryAssetDownloadButton from '@/medical-billing-summaries/asset/components/MedSummaryAssetDownloadButton'
import { matterPaths } from '@/routing/paths'
import { Header } from '@/components/header/Header'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'

/**
 * Breadcrumbs for the living Medical Billing Summary: Matter > Medical Billing Summary,
 * with Ask Assistant + Download actions once the summary has entries. Sub-pages (Add Files, Previous
 * summaries) add their own crumb and make the summary crumb a clickable parent.
 */
export default function MatterBreadcrumbsMedSummaryAsset({ subPageCrumb }: { subPageCrumb?: string } = {}) {
  const navigate = useNavigate()

  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const initialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: activeMatterId }))
  const asset = useAppSelector((state: RootState) => selectMedSummaryAssetForMatter(state, { matterId: activeMatterId ?? undefined }))

  const breadcrumbItems: BreadcrumbItemType[] = useMemo(() => {
    if (!initialLoadComplete) return []

    const items: BreadcrumbItemType[] = []
    if (matter) {
      items.push({
        id: `matter-${matter.id}`,
        label: matter.name,
        onClick: () => navigate(matterPaths.overview(matter.id)),
      })
    }
    items.push({
      id: 'med-summary-asset',
      label: 'Medical Billing Summary',
      // On sub-pages, Medical Billing Summary becomes a clickable parent crumb.
      ...(subPageCrumb && activeMatterId ? { onClick: () => navigate(matterPaths.billing.index(activeMatterId)) } : {}),
    })
    if (subPageCrumb) {
      items.push({ id: 'sub-page', label: subPageCrumb })
    }
    return items
  }, [initialLoadComplete, matter, navigate, subPageCrumb, activeMatterId])

  const showActions = asset && asset.total_entries > 0 && activeMatterId

  return (
    <Header
      breadcrumbs={breadcrumbItems}
      endContent={
        showActions ? (
          <div className="hidden lg:flex items-center gap-3.5">
            <MedSummaryAssetChatButton assetId={asset.id} matterId={activeMatterId} />
            <MedSummaryAssetDownloadButton assetId={asset.id} />
          </div>
        ) : undefined
      }
    />
  )
}
